import { type FC, useState, useEffect, useMemo, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { ArrowRight, Loader2, Sparkles } from "lucide-react";

import { NavPill } from "@/pages/layouts/components/NavPill";

import { Button } from "@/shared/components/ui/button";
import { isValidEmail } from "@/shared/utils/validators";
import { Input } from "@/shared/components/ui/input";
import { FieldError } from "@/shared/components/ui/FieldError";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/shared/components/ui/dialog";
import { useOnboardingAuth } from "@/shared/hooks/useOnboardingAuth";
import { getHelpTopicsFromAnswers } from "@/shared/constants/helpTopics";
import { getOnboardingFlow } from "@/shared/utils/funnelAnalytics";
import logoFull from "@/shared/assets/logo/logo-full.svg";

import type { FlowCustomComponentProps } from "@/shared/constants/flowTypes";

import { getRefPillText } from "@/shared/constants/constants";

import { getOrderedStories } from "@/pages/onboarding/authStories";
import { StoryCarousel } from "@/pages/onboarding/components/StoryCarousel";
import { OtpInput } from "@/shared/components/ui/OtpInput";
import { SecurityNote } from "@/shared/components/ui/security-note";

const OTP_LENGTH = 6;

export const VerifyEmail: FC<FlowCustomComponentProps> = ({ onComplete, answers }) => {
  const [otp, setOtp] = useState("");
  const [isEditingEmail, setIsEditingEmail] = useState(false);
  const [editEmail, setEditEmail] = useState("");
  const [emailError, setEmailError] = useState<string | null>(null);

  const [searchParams] = useSearchParams();
  const storyParam = searchParams.get("story");
  const refParam = (answers["onboarding-utm"] as string) || searchParams.get("utm_source") || "";
  const pillText = getRefPillText(refParam);
  const orderedStories = useMemo(() => getOrderedStories(storyParam), [storyParam]);

  const {
    verifyOtpAndCreateUser,
    resendVerificationCode,
    updateEmailAndResend,
    isLoading,
    error,
    clearError,
    pendingEmail,
  } = useOnboardingAuth({
    analyticsFlow: getOnboardingFlow(answers),
  });

  const isComplete = otp.length === OTP_LENGTH;

  const obituaryLink = (answers["obit-link"] as string) || undefined;
  const helpTopics = getHelpTopicsFromAnswers(answers) ?? undefined;

  const handleVerify = useCallback(async () => {
    if (isLoading || !isComplete) return;

    clearError();

    const userRecord = await verifyOtpAndCreateUser(otp, {
      fullName: "",
      phoneNumber: "",
      obituaryLink,
      helpTopics,
    });

    if (userRecord) {
      onComplete();
    }
  }, [
    otp,
    isComplete,
    isLoading,
    clearError,
    verifyOtpAndCreateUser,
    obituaryLink,
    helpTopics,
    onComplete,
  ]);

  useEffect(() => {
    if (isComplete && !isLoading) {
      handleVerify();
    }
  }, [isComplete, isLoading, handleVerify]);

  const handleOtpChange = (value: string) => {
    clearError();
    setOtp(value);
  };

  const handleResendCode = async () => {
    clearError();
    setOtp("");
    await resendVerificationCode();
  };

  const handleOpenChangeEmail = () => {
    setEditEmail(pendingEmail ?? "");
    setEmailError(null);
    setIsEditingEmail(true);
  };

  const handleUpdateEmail = async () => {
    const trimmed = editEmail.trim();
    const isValid = isValidEmail(trimmed);

    if (!trimmed) {
      setEmailError("Enter your email address");
      return;
    }
    if (!isValid) {
      setEmailError("Enter a valid email address");
      return;
    }

    clearError();
    setOtp("");
    const success = await updateEmailAndResend(trimmed);
    if (success) {
      setIsEditingEmail(false);
      setEmailError(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex h-screen bg-subtle">
      <div className="flex flex-1 flex-col overflow-y-auto px-4 py-6 sm:px-8 sm:py-8">
        <img src={logoFull} alt="Elayne" className="h-7 self-start" />

        <div className="flex flex-1 items-center justify-center">
          <div className="flex w-full max-w-md flex-col items-center gap-6 sm:gap-10">
            <div className="flex flex-col items-center gap-3">
              <NavPill icon={Sparkles} label={pillText} variant="highlight" />
              <h2>Check your inbox</h2>
              {pendingEmail && (
                <p className="text-center text-sm text-secondary">
                  We sent a 6-digit code to{" "}
                  <span className="break-all font-medium text-primary">{pendingEmail}</span>{" "}
                  <button
                    type="button"
                    onClick={handleOpenChangeEmail}
                    className="font-medium text-brand underline"
                  >
                    Change email
                  </button>
                </p>
              )}
            </div>

            <div className="flex flex-col items-center gap-6">
              <OtpInput
                value={otp}
                onChange={handleOtpChange}
                disabled={isLoading}
                error={!!error}
                className="gap-1.5 sm:gap-2"
              />

              <p className="text-sm text-secondary">
                {error ? (
                  <>
                    <span className="text-secondary">{error}.</span>{" "}
                    <Button
                      variant="link"
                      size={null}
                      type="button"
                      onClick={handleResendCode}
                      disabled={isLoading}
                      className="inline text-sm text-destructive underline"
                    >
                      Resend code
                    </Button>
                  </>
                ) : (
                  <>
                    Didn't get it? Check your spam folder or{" "}
                    <Button
                      variant="link"
                      size={null}
                      type="button"
                      onClick={handleResendCode}
                      disabled={isLoading}
                      className="inline text-sm text-brand underline"
                    >
                      resend code
                    </Button>
                  </>
                )}
              </p>
            </div>

            <Button
              variant="brand"
              rounded={true}
              onClick={handleVerify}
              disabled={!isComplete || isLoading}
              className="w-full"
            >
              {isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  Continue <ArrowRight className="h-4 w-4" />
                </>
              )}
            </Button>

            <SecurityNote text="Your information is encrypted and never sold." />
          </div>
        </div>
      </div>

      <div className="hidden p-5 pl-0 lg:flex lg:w-1/3">
        <StoryCarousel stories={orderedStories} className="flex-1" />
      </div>

      <Dialog
        open={isEditingEmail}
        onOpenChange={(open) => {
          if (!open) {
            setIsEditingEmail(false);
            setEmailError(null);
          }
        }}
      >
        <DialogContent size="sm">
          <DialogHeader>
            <DialogTitle>Update your email address</DialogTitle>
          </DialogHeader>
          <Input
            id="update-email"
            type="email"
            value={editEmail}
            onChange={(e) => {
              setEditEmail(e.target.value);
              setEmailError(null);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleUpdateEmail();
            }}
            className={emailError ? "border-destructive" : ""}
            disabled={isLoading}
          />
          {emailError && <FieldError message={emailError} className="-mt-4" />}
          <DialogFooter
            onSubmit={handleUpdateEmail}
            submitText="Resend code"
            loading={isLoading}
            disabled={isLoading}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};
